const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/uster/eCheckin/sections/Versicherung.tsx';
import React, { forwardRef } from 'react';
import _ from 'lodash';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { Divider } from '@mui/material';
import { useText } from '@cistec/common_web/utils/intlUtils';
import { useCIS4m, useCIS4mData } from '@cistec/common_web/modules/CIS4m/hooks';
import { CIS4mSingleCheckbox } from '@cistec/common_web/modules/CIS4m/components/CIS4mSingleCheckbox';
import { CIS4mTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mTextField';
import { CIS4mDatePicker } from '@cistec/common_web/modules/CIS4m/components/CIS4mDatePicker';
import { CIS4mBlock } from '@cistec/common_web/modules/CIS4m/components/CIS4mBlock';
import textMapCommon from '../../../common/translations';
import textMap from 'App/config/uster/eCheckin/translations';
import { createFieldPath } from 'App/components/eCheckin/utils';
import ECheckinSection from 'App/components/eCheckin/components/ECheckinSection';
import { ECheckinSectionProps } from 'App/components/eCheckin/types';
import { BehandlungsgrundField } from 'App/components/eCheckin/components/fields/metakat/BehandlungsgrundField';
import { VersicherungsdeckungField } from 'App/components/eCheckin/components/fields/metakat/VersicherungsdeckungField';
import { YesNoField } from 'App/components/eCheckin/components/fields/YesNoField';
import { AddressBlock, AddressFieldsConfig } from 'App/components/eCheckin/components/fields/AddressBlock';
import { PhoneNumberField } from 'App/components/eCheckin/components/fields/PhoneNumberField';
import { LabelLink } from 'App/components/eCheckin/components/fields/LabelLink';
import { KostentraegerField } from './components/metakat/Versicherung/KostentraegerField';
import { CIS4mMaskedTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mMaskedTextField';

const addressFieldsConfig: AddressFieldsConfig = {
    additionalInfo: {
        included: false,
    },
    country: {
        included: false,
    },
};

export const Versicherung = forwardRef((props: ECheckinSectionProps, ref: any) => {
    const { path } = props;
    const title = useText(props.title);

    const eCheckinSectionRef = useCIS4m(ref);
    const formData = useCIS4mData();
    const { formatMessage } = useIntl();

    const PATH_REASON = createFieldPath(path, 'reason');
    const PATH_ACCIDENT_DATE = createFieldPath(path, 'accidentDate');
    const PATH_COST_UNIT = createFieldPath(path, 'costUnit');

    const PATH_INSURANCE = createFieldPath(path, 'insurance');
    const PATH_INSURANCE_POLICY_NO = createFieldPath(path, 'insurancePolicyNo');
    const PATH_INSURANCE_CARD_NO = createFieldPath(path, 'insuranceCardNo');

    const PATH_ADDITIONAL_INSURANCE = createFieldPath(path, 'additionalInsurance');
    const PATH_ADDITIONAL_INSURANCE_NAME = createFieldPath(path, 'additionalInsuranceName');
    const PATH_ADDITIONAL_INSURANCE_NO = createFieldPath(path, 'additionalInsuranceNo');

    const PATH_COMPANY = createFieldPath(path, 'company');
    const PATH_ACCIDENT_INSURANCE = createFieldPath(path, 'accidentInsurance');
    const PPATH_ACCIDENT_INSURANCE_NO = createFieldPath(path, 'accidentInsuranceNo');
    const PATH_TEL = createFieldPath(path, 'tel');
    const PATH_COUNTRY = createFieldPath(path, 'country');

    const PATH_ADDITIONAL_AMBULANT = createFieldPath(path, 'additionalAmbulant');
    const PATH_ADDITIONAL_AMBULANT_NAME = createFieldPath(path, 'additionalAmbulantName');

    const PATH_CLASS = createFieldPath(path, 'class');
    const PATH_COMFORT_UPGRADE = createFieldPath(path, 'comfortUpgrade');

    const reason = _.get(formData, PATH_REASON);
    const isReasonDesire = reason === 'Wunscheingriff';
    const isAccident = reason === 'Unfall';

    const costUnit = _.get(formData, PATH_COST_UNIT);
    const hideInsuranceBlock = ['Unfallversicherung', undefined].includes(costUnit);
    const hideAdditionalInsurance = ['Selbstzahler', undefined].includes(costUnit);
    const isSelfpayPatient = costUnit === 'Selbstzahler';
    const isAccidentInsurance = costUnit === 'Unfallversicherung';

    const insuranceClass = _.get(formData, PATH_CLASS);
    const isClassSemiPrivate = insuranceClass === 'halbprivat';
    const hideClassInfomation = !['halbprivat', 'privat'].includes(insuranceClass);

    const hasAdditionalInsurance = _.get(formData, PATH_ADDITIONAL_INSURANCE) === 'yes';
    const hasAdditionalAmbulant = _.get(formData, PATH_ADDITIONAL_AMBULANT) === 'yes';
    const isComfortUpgrade = _.get(formData, PATH_COMFORT_UPGRADE) === '1';

    return (
        <ECheckinSection ref={eCheckinSectionRef} path={path} title={title}>
            <h1>{title}</h1>
            <BehandlungsgrundField label={textMap.reason} path={PATH_REASON} required={true} />
            {isReasonDesire && <div>{formatMessage(textMap.desiredText)} </div>}
            <CIS4mDatePicker
                label={formatMessage(textMap.accidentDate)}
                path={PATH_ACCIDENT_DATE}
                maxDate={moment()}
                required={true}
                hidden={!isAccident}
            />
            <KostentraegerField label={textMap.costUnit} path={PATH_COST_UNIT} required={true} />
            {isSelfpayPatient && <div>{formatMessage(textMap.selfPayPatientText)}</div>}

            <CIS4mBlock hidden={hideInsuranceBlock}>
                <CIS4mTextField label={textMap.insuranceCompany} path={PATH_INSURANCE} required={!isSelfpayPatient} />
                <CIS4mTextField label={textMap.policyNo} path={PATH_INSURANCE_POLICY_NO} required={!isSelfpayPatient} />
                <CIS4mMaskedTextField
                    label={textMap.insuranceCardNo}
                    path={PATH_INSURANCE_CARD_NO}
                    mask={'80999999999999999999'}
                    required={!isSelfpayPatient}
                />
                <Divider />
            </CIS4mBlock>
            <CIS4mBlock hidden={hideAdditionalInsurance}>
                <YesNoField
                    label={textMap.additionalInsurance}
                    path={PATH_ADDITIONAL_INSURANCE}
                    yesValue="yes"
                    noValue="no"
                    required={true}
                />
                <CIS4mTextField
                    label={textMap.additionalInsuranceName}
                    path={PATH_ADDITIONAL_INSURANCE_NAME}
                    required={true}
                    hidden={!hasAdditionalInsurance}
                />
                <CIS4mTextField
                    label={textMap.additionalInsuranceNameNo}
                    path={PATH_ADDITIONAL_INSURANCE_NO}
                    required={true}
                    hidden={!hasAdditionalInsurance}
                />
                <Divider />
            </CIS4mBlock>

            <CIS4mBlock hidden={!isAccidentInsurance}>
                <CIS4mTextField label={textMap.company} path={PATH_COMPANY} required={true} maxLength={30} />
                <AddressBlock basePath={path} fieldsConfig={addressFieldsConfig} required={true} />
                <PhoneNumberField
                    label={textMapCommon.tel}
                    path={PATH_TEL}
                    required={true}
                    countryCodePath={PATH_COUNTRY}
                />
                <CIS4mTextField label={textMap.accidentInsurance} path={PATH_ACCIDENT_INSURANCE} required={true} />
                <CIS4mTextField
                    label={textMap.accidentInsuranceNo}
                    path={PPATH_ACCIDENT_INSURANCE_NO}
                    required={true}
                />
                <Divider />
            </CIS4mBlock>

            <VersicherungsdeckungField label={textMap.class} path={PATH_CLASS} required={true} />

            <CIS4mBlock hidden={!isClassSemiPrivate}>
                <div>
                    <strong>{formatMessage(textMap.comfortUpgradeHeadline)}</strong>
                </div>
                <div>
                    {formatMessage(textMap.comfortUpgradeTextPartOne, {
                        link: <LabelLink text="www.spitaluster.ch/kovo" url="https://www.spitaluster.ch/kovo" />,
                    })}
                </div>
                <div>{formatMessage(textMap.comfortUpgradeTextPartTwo)}</div>
                <CIS4mSingleCheckbox label={textMap.comfortUpgradeCheckbox} path={PATH_COMFORT_UPGRADE} />
                {isComfortUpgrade && <div>{formatMessage(textMap.comfortUpgradeCheckboxText)} </div>}
                <Divider />
            </CIS4mBlock>
            <CIS4mBlock hidden={hideClassInfomation}>
                <div dangerouslySetInnerHTML={{ __html: formatMessage(textMap.insuranceText) }} />
                <Divider />
            </CIS4mBlock>

            <YesNoField
                label={textMap.additionalAmbulant}
                path={PATH_ADDITIONAL_AMBULANT}
                yesValue="yes"
                noValue="no"
                required={true}
            />
            <CIS4mTextField
                label={textMap.additionalAmbulantName}
                path={PATH_ADDITIONAL_AMBULANT_NAME}
                required={true}
                hidden={!hasAdditionalAmbulant}
            />
        </ECheckinSection>
    );
});

Versicherung.displayName = 'Versicherung';

export default Versicherung;
